/* Respecto mobile search placeholder typewriter — 2026-08-14 */
(() => {
  const mobileQuery = window.matchMedia('(max-width: 749px)');
  const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  const states = new WeakMap();
  const pendingStarts = new WeakMap();
  const registeredInputs = new Set();

  const cancelPendingStart = (input) => {
    const timer = pendingStarts.get(input);
    if (timer !== undefined) {
      window.clearTimeout(timer);
      pendingStarts.delete(input);
    }
  };

  const clearState = (input, restore = true) => {
    cancelPendingStart(input);
    const state = states.get(input);
    if (state) {
      state.timers.forEach((timer) => window.clearTimeout(timer));
      state.timers.clear();
      states.delete(input);
    }

    input.classList.remove('respecto-typewriter-active');

    if (restore && !input.value) {
      const fullText = input.dataset.respectoTypewriterText;
      if (fullText) input.placeholder = fullText;
    }
  };

  const scheduleStart = (input, callback, delay = 70) => {
    cancelPendingStart(input);
    const timer = window.setTimeout(() => {
      pendingStarts.delete(input);
      callback();
    }, delay);
    pendingStarts.set(input, timer);
  };

  const schedule = (state, callback, delay) => {
    const timer = window.setTimeout(() => {
      state.timers.delete(timer);
      callback();
    }, delay);
    state.timers.add(timer);
  };

  const startTypewriter = (input) => {
    if (!(input instanceof HTMLInputElement)) return;

    const existingText = input.dataset.respectoTypewriterText || input.placeholder;
    if (existingText) input.dataset.respectoTypewriterText = existingText;

    clearState(input, false);

    if (
      !mobileQuery.matches ||
      reducedMotionQuery.matches ||
      input.value ||
      (input.id === 'cmm-search-input' && document.activeElement === input) ||
      !input.dataset.respectoTypewriterText
    ) {
      clearState(input, true);
      return;
    }

    const fullText = input.dataset.respectoTypewriterText;
    const useAnimatedCursor = true;
    const cursor = useAnimatedCursor ? '▍' : '';
    const state = { timers: new Set(), index: 0, lastTick: performance.now() };
    states.set(input, state);
    input.classList.add('respecto-typewriter-active');
    input.placeholder = cursor;

    const typeNextCharacter = () => {
      const now = performance.now();
      if (
        states.get(input) !== state ||
        input.value ||
        (input.id === 'cmm-search-input' && document.activeElement === input) ||
        now - state.lastTick > 300
      ) {
        clearState(input, true);
        return;
      }

      state.lastTick = now;
      state.index += 1;
      input.placeholder = fullText.slice(0, state.index) + cursor;

      if (state.index < fullText.length) {
        schedule(state, typeNextCharacter, 40);
        return;
      }

      // The burger search cursor must never remain inside the completed placeholder.
      // Remove it synchronously after the final character; interaction guards still
      // restore the original placeholder immediately on touch, focus, or typing.
      if (input.id === 'cmm-search-input') {
        input.placeholder = fullText;
        clearState(input, true);
        return;
      }

      if (!useAnimatedCursor) {
        schedule(state, () => {
          if (states.get(input) !== state) return;
          clearState(input, true);
        }, 120);
        return;
      }

      schedule(state, () => {
        if (states.get(input) !== state) return;
        input.placeholder = fullText;
      }, 180);

      schedule(state, () => {
        if (states.get(input) !== state) return;
        input.placeholder = fullText + '▍';
      }, 340);

      schedule(state, () => {
        if (states.get(input) !== state) return;
        clearState(input, true);
      }, 520);
    };

    schedule(state, typeNextCharacter, 90);
  };

  const registerInput = (input, { stopOnFocus = false } = {}) => {
    if (!(input instanceof HTMLInputElement) || registeredInputs.has(input)) return;
    registeredInputs.add(input);

    const stopForInteraction = () => clearState(input, true);
    input.addEventListener('pointerdown', stopForInteraction, { passive: true });
    input.addEventListener('touchstart', stopForInteraction, { passive: true });
    input.addEventListener('keydown', stopForInteraction);
    input.addEventListener('input', stopForInteraction);
    input.addEventListener('compositionstart', stopForInteraction);
    if (stopOnFocus) input.addEventListener('focus', stopForInteraction);
  };

  const cmmDetails = document.querySelector('#Details-menu-drawer-container');
  const cmmInput = document.querySelector('#cmm-search-input');
  const cmmForm = cmmInput?.closest('.cmm-search-form');
  const cmmOverlay = cmmForm?.querySelector('[data-cmm-typewriter-overlay]');
  let cmmFrame = 0;
  let cmmRun = 0;

  const stopCmmTypewriter = () => {
    cmmRun += 1;
    if (cmmFrame) window.cancelAnimationFrame(cmmFrame);
    cmmFrame = 0;
    cmmForm?.classList.remove('respecto-typewriter-overlay-active');
    if (cmmOverlay) cmmOverlay.textContent = '';
  };

  const startCmmTypewriter = () => {
    stopCmmTypewriter();

    if (
      !cmmDetails?.hasAttribute('open') ||
      !(cmmInput instanceof HTMLInputElement) ||
      !(cmmForm instanceof HTMLElement) ||
      !(cmmOverlay instanceof HTMLElement) ||
      !mobileQuery.matches ||
      reducedMotionQuery.matches ||
      cmmInput.value ||
      document.activeElement === cmmInput
    ) {
      return;
    }

    const fullText = cmmInput.placeholder;
    if (!fullText) return;

    const run = cmmRun;
    const initialDelay = 90;
    const characterDelay = 40;
    let startedAt;

    cmmForm.classList.add('respecto-typewriter-overlay-active');
    cmmOverlay.textContent = '▍';

    const renderFrame = (now) => {
      if (
        run !== cmmRun ||
        !cmmDetails.hasAttribute('open') ||
        cmmInput.value ||
        document.activeElement === cmmInput
      ) {
        stopCmmTypewriter();
        return;
      }

      if (startedAt === undefined) startedAt = now;
      const elapsed = now - startedAt;

      if (elapsed < initialDelay) {
        cmmFrame = window.requestAnimationFrame(renderFrame);
        return;
      }

      const index = Math.min(fullText.length, Math.floor((elapsed - initialDelay) / characterDelay) + 1);
      cmmOverlay.textContent = fullText.slice(0, index) + (index < fullText.length ? '▍' : '');

      if (index >= fullText.length) {
        stopCmmTypewriter();
        return;
      }

      cmmFrame = window.requestAnimationFrame(renderFrame);
    };

    cmmFrame = window.requestAnimationFrame(renderFrame);
  };

  if (cmmDetails && cmmInput && cmmForm && cmmOverlay) {
    const stopForInteraction = () => stopCmmTypewriter();
    cmmInput.addEventListener('pointerdown', stopForInteraction, { passive: true });
    cmmInput.addEventListener('touchstart', stopForInteraction, { passive: true });
    cmmInput.addEventListener('focus', stopForInteraction);
    cmmInput.addEventListener('keydown', stopForInteraction);
    cmmInput.addEventListener('input', stopForInteraction);
    cmmInput.addEventListener('compositionstart', stopForInteraction);

    cmmDetails.addEventListener('toggle', () => {
      stopCmmTypewriter();
      if (cmmDetails.hasAttribute('open')) {
        cmmFrame = window.requestAnimationFrame(() => {
          cmmFrame = window.requestAnimationFrame(startCmmTypewriter);
        });
      }
    });

    if (cmmDetails.hasAttribute('open')) startCmmTypewriter();
  }

  const searchDialog = document.querySelector('.search-modal__content');
  const modalInput = searchDialog?.querySelector('.search-input');

  if (searchDialog && modalInput) {
    registerInput(modalInput);

    const syncModalState = () => {
      if (searchDialog.hasAttribute('open')) {
        scheduleStart(modalInput, () => {
          if (!searchDialog.hasAttribute('open')) {
            clearState(modalInput, true);
            return;
          }
          startTypewriter(modalInput);
        });
      } else {
        clearState(modalInput, true);
      }
    };

    new MutationObserver(syncModalState).observe(searchDialog, {
      attributes: true,
      attributeFilter: ['open'],
    });

    if (searchDialog.hasAttribute('open')) syncModalState();
  }

  const restoreAll = () => {
    stopCmmTypewriter();
    registeredInputs.forEach((input) => clearState(input, true));
  };

  mobileQuery.addEventListener?.('change', restoreAll);
  reducedMotionQuery.addEventListener?.('change', restoreAll);
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) restoreAll();
  });
  window.addEventListener('pagehide', restoreAll);
})();
